import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { FloppyDisk, Link, ArrowsClockwise, Info, Folder } from "@phosphor-icons/react";

export default function SettingsPage() {
  const [rootFolderId, setRootFolderId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setRootFolderId(data.root_folder_id || "");
    } catch (e) {
      console.error("load settings failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!rootFolderId.trim()) {
      toast.error("Введите ID корневой папки");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings", { root_folder_id: rootFolderId.trim() });
      toast.success("Настройки сохранены");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const scan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const { data } = await api.post("/files/scan");
      setScanResult(data);
      toast.success(
        `Сканирование: ${data.folders} папок, добавлено ${data.added}, обновлено ${data.updated}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка сканирования");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="mono-label mb-2">SECTION / SETTINGS</div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Настройки</h1>
      <p className="text-sm text-[#52525b] mt-2 max-w-xl">
        Укажите корневую папку на Google Drive, внутри которой лежат папки-сайты с прайсами.
      </p>

      {/* Folder ID */}
      <section className="mt-8 bg-white border border-[#d4d4d8] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Folder size={18} weight="bold" className="text-[#0047FF]" />
          <h2 className="text-lg font-medium tracking-tight">Корневая папка Drive</h2>
        </div>

        <label className="mono-label block mb-2" htmlFor="folder-id-input">
          FOLDER ID
        </label>
        <input
          id="folder-id-input"
          data-testid="settings-folder-id-input"
          value={rootFolderId}
          onChange={(e) => setRootFolderId(e.target.value)}
          placeholder="напр. 1A2B3C...xyz (скопируйте из URL папки в Drive)"
          className="w-full font-mono text-sm border border-[#d4d4d8] px-3 py-2.5 bg-white focus:outline-none focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF]"
          disabled={loading}
        />

        <div className="mt-3 flex items-start gap-2 text-xs text-[#52525b]">
          <Info size={14} weight="bold" className="mt-0.5 shrink-0" />
          <span>
            Откройте папку в Google Drive — в URL после <code className="mono">/folders/</code> и есть ID.
            Пример: <code className="mono">drive.google.com/drive/folders/<b>1A2B3C...xyz</b></code>
          </span>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save-button"
            className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors duration-150 disabled:opacity-50"
          >
            <FloppyDisk size={14} weight="bold" />
            {saving ? "Сохранение..." : "Сохранить"}
          </button>
          <button
            onClick={scan}
            disabled={scanning || !rootFolderId.trim()}
            data-testid="settings-scan-button"
            className="inline-flex items-center gap-2 border border-[#18181b] bg-transparent text-[#18181b] px-4 py-2 text-sm hover:bg-[#18181b] hover:text-white transition-colors duration-150 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" className={scanning ? "animate-spin" : ""} />
            {scanning ? "Сканирование..." : "Сканировать Drive"}
          </button>
        </div>

        {scanResult && (
          <div
            className="mt-5 border border-[#d4d4d8] bg-[#f5f5f4] p-4"
            data-testid="scan-result"
          >
            <div className="mono-label mb-2">РЕЗУЛЬТАТ СКАНИРОВАНИЯ</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Папок" value={scanResult.folders} />
              <Stat label="Добавлено" value={scanResult.added} />
              <Stat label="Обновлено" value={scanResult.updated} />
              <Stat label="Пропущено" value={scanResult.skipped} />
            </div>
          </div>
        )}
      </section>

      {/* Info block */}
      <section className="mt-8 bg-white border border-[#d4d4d8] p-6">
        <div className="flex items-center gap-2 mb-3">
          <Link size={18} weight="bold" className="text-[#0047FF]" />
          <h2 className="text-lg font-medium tracking-tight">Как это работает</h2>
        </div>
        <ol className="text-sm text-[#52525b] space-y-2 list-decimal pl-5">
          <li>Укажите ID корневой папки — здесь лежат папки-сайты.</li>
          <li>Нажмите «Сканировать Drive» — приложение найдёт все прайс-листы.</li>
          <li>В разделе «Прайс-листы» настройте маппинг столбцов для каждого файла.</li>
          <li>В разделе «Добавить строку» вводите данные и отмечайте нужные файлы.</li>
        </ol>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#d4d4d8] bg-white px-3 py-2">
      <div className="mono-label">{label}</div>
      <div className="font-mono text-xl font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
